import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, ArrowRight, TrendingUp } from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';

import { BACKEND_URL_EXPORT as BACKEND_URL } from '../../config';

const BlogPage = () => {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState('USD');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/blog/posts`);

      const normalized = (response.data || []).map((post) => {
        const createdAt = post.created_at ? new Date(post.created_at) : null;
        const date = createdAt && !Number.isNaN(createdAt.getTime())
          ? createdAt.toISOString().slice(0, 10)
          : (post.date || new Date().toISOString().slice(0, 10));

        const words = (post.content || '').toString().trim().split(/\s+/).filter(Boolean).length;
        const minutes = Math.max(1, Math.round(words / 200));

        return {
          id: post.id,
          slug: post.slug || post.id,
          title: post.title,
          excerpt: post.excerpt,
          category: post.category || 'Industry News',
          date,
          readTime: post.readTime || `${minutes} min read`,
          image: post.image_url || post.image || 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800',
          featured: Boolean(post.featured),
          // Keep original payload to support newer blog post detail pages
          content: post.content,
          image_url: post.image_url
        };
      });

      setBlogPosts(normalized);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      // Fallback to sample data if API fails
      setBlogPosts(fallbackPosts);
    } finally {
      setLoading(false);
    }
  };

  // Fallback sample posts for initial display
  const fallbackPosts = [
    {
      id: '1',
      slug: 'guide-to-buying-japanese-used-cars',
      title: 'Complete Guide to Buying Japanese Used Cars',
      excerpt: 'Everything you need to know about purchasing quality used vehicles from Japan. From inspection to shipping, we cover it all.',
      category: 'Buying Guide',
      date: '2024-12-10',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800',
      featured: true
    },
    {
      id: '2',
      slug: 'understanding-japanese-auction-grades',
      title: 'Understanding Japanese Auction Grades',
      excerpt: 'Learn how to read and understand Japanese auction grading systems to make informed vehicle purchase decisions.',
      category: 'Education',
      date: '2024-12-08',
      readTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1486496146582-9ffcd0b2b2b7?w=800',
      featured: false
    },
    {
      id: '3',
      slug: 'top-5-reliable-japanese-cars-2024',
      title: 'Top 5 Most Reliable Japanese Cars in 2024',
      excerpt: 'Discover which Japanese vehicles offer the best reliability, value, and performance for international buyers.',
      category: 'Reviews',
      date: '2024-12-05',
      readTime: '10 min read',
      image: 'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800',
      featured: true
    },
    {
      id: '4',
      slug: 'shipping-costs-explained',
      title: 'Vehicle Shipping Costs: A Complete Breakdown',
      excerpt: 'Understand all the costs involved in shipping a vehicle from Japan to your destination country.',
      category: 'Shipping',
      date: '2024-12-01',
      readTime: '7 min read',
      image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800',
      featured: false
    },
    {
      id: '5',
      slug: 'import-regulations-guide',
      title: 'Import Regulations: What You Need to Know',
      excerpt: 'Navigate import regulations and customs requirements when importing vehicles from Japan.',
      category: 'Legal',
      date: '2024-11-28',
      readTime: '9 min read',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800',
      featured: false
    },
    {
      id: '6',
      slug: 'maintenance-tips-japanese-vehicles',
      title: 'Essential Maintenance Tips for Japanese Vehicles',
      excerpt: 'Keep your Japanese vehicle running smoothly with these expert maintenance tips and schedules.',
      category: 'Maintenance',
      date: '2024-11-25',
      readTime: '5 min read',
      image: 'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=800',
      featured: false
    }
  ];

  const categories = ['All', 'Industry News', 'Buying Guide', 'Maintenance Tips', 'Company Updates', 'Education', 'Reviews', 'Shipping'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Blog - Japanese Vehicle Export Insights | Global Trade Car"
        description="Expert insights, guides, and tips on buying and importing Japanese vehicles. Stay updated with the latest industry news."
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4">
            <TrendingUp className="h-4 w-4 mr-1" />
            Latest Updates
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Blog & Insights
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert guides, tips, and industry insights for buying Japanese vehicles
          </p>
        </div>

        {/* Featured Posts */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredPosts.slice(0, 2).map((post) => (
              <Card 
                key={post.id} 
                className="cursor-pointer hover:shadow-xl transition-shadow overflow-hidden group"
                onClick={() => navigate(`/blog/${post.slug || post.id}`)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800';
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-red-600 text-white">{post.category}</Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.readTime}
                      </span>
                    </div>
                    <ArrowRight className="h-5 w-5 text-red-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* All Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => (
            <Card 
              key={post.id}
              className="cursor-pointer hover:shadow-xl transition-shadow overflow-hidden group"
              onClick={() => navigate(`/blog/${post.slug}`)}
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800';
                  }}
                />
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="bg-white/90">{post.category}</Badge>
                </div>
              </div>
              <CardContent className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className="flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    {post.readTime}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Newsletter CTA */}
        <div className="mt-16">
          <Card className="bg-gradient-to-r from-red-600 to-red-700 text-white">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
              <p className="text-xl text-red-100 mb-6 max-w-2xl mx-auto">
                Get the latest articles, tips, and vehicle listings delivered to your inbox
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900"
                />
                <button className="px-6 py-3 bg-white text-red-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold">
                  Subscribe
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default BlogPage;